import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight, Play } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { getBlogPost } from '../lib/api';
import { sized } from '../lib/img';
import BlogEngagement from '../components/BlogEngagement';
import './blogContent.css';

function LegacyBody({ body }) {
  return (
    <div className="mt-8 space-y-5">
      {body.map((para, i) => (
        para.startsWith('## ')
          ? <h2 key={i} className="font-display font-bold text-[#0E1726] text-[24px] tracking-tight pt-2">{para.replace('## ', '')}</h2>
          : <p key={i} className="text-[#334155] text-[17px] leading-[1.75]">{para}</p>
      ))}
    </div>
  );
}

export default function BlogPost() {
  const ref = useReveal();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => { getBlogPost(slug).then(setPost).catch(() => setPost(false)); }, [slug]);

  if (post === false) return <div className="max-w-3xl mx-auto px-5 pt-20 text-center text-[#5B6472]">Article not found. <Link to="/blog" className="text-[#2E6BFF]">Back to resources</Link></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-5 pt-20 text-center text-[#5B6472]">Loading...</div>;

  const isVideo = post.type === 'video';

  return (
    <div ref={ref} className="max-w-3xl mx-auto px-5 sm:px-8 pt-10">
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-[#5B6472] hover:text-[#2E6BFF] text-sm font-medium"><ArrowLeft size={16} /> Back to resources</Link>
      <div className="reveal in mt-5">
        <span className="text-xs font-semibold text-[#2E6BFF] inline-flex items-center gap-1.5">
          {isVideo && <Play size={12} fill="currentColor" />}{post.tag}
        </span>
        <h1 data-testid="post-title" className="font-display font-extrabold text-[#0E1726] text-[34px] sm:text-[44px] tracking-tight leading-[1.05] mt-3">{post.title}</h1>
        <div className="flex items-center gap-4 mt-4 text-sm text-[#5B6472]">
          <span>By {post.author}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> {post.read_time}</span>
        </div>
      </div>

      {isVideo ? (
        post.video_embed ? (
          <div data-testid="post-video" className="reveal in rounded-[20px] overflow-hidden mt-7 bg-black">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                title={post.title}
                src={post.video_embed}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        ) : (
          <a data-testid="post-video-link" href={post.video_url} target="_blank" rel="noreferrer" className="reveal in mt-7 flex items-center justify-center gap-2 bg-[#0E1726] text-white font-semibold py-4 rounded-2xl">
            <Play size={18} /> Watch the video
          </a>
        )
      ) : (
        <>
          {post.image && (
            <div className="reveal in rounded-[20px] overflow-hidden mt-7">
              <img src={sized(post.image, 1000)} alt={post.title} loading="lazy" decoding="async" className="w-full h-[320px] object-cover" />
            </div>
          )}
          <div className="reveal in">
            {Array.isArray(post.body)
              ? <LegacyBody body={post.body} />
              : <div data-testid="post-body" className="blog-content mt-8" dangerouslySetInnerHTML={{ __html: post.body_html || '' }} />}
          </div>
        </>
      )}

      {isVideo && post.excerpt && (
        <p className="reveal in text-[#334155] text-[17px] leading-[1.75] mt-7">{post.excerpt}</p>
      )}

      <BlogEngagement slug={post.slug} />

      <div className="mt-12 bg-[#2E6BFF] rounded-[24px] p-8 sm:p-10 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5">
        <div>
          <h3 className="font-display font-extrabold text-[24px]">Want to go deeper?</h3>
          <p className="text-white/85 mt-2">Bring your real challenge to a 1:1 session with Rajni.</p>
        </div>
        <Link to="/contact" className="bg-white text-[#0E1726] font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap">Talk to Rajni <ArrowRight size={17} /></Link>
      </div>
    </div>
  );
}
